/*
 * Schedule on a phone, like the old Steward's (05-calendar.js calTeamHTML, owner 2026-10-29: "schedule should look like what
 * I showed"): the month on a big button (tap it for the month to pick a day), Today and View options on the right, the
 * week as a strip of days (swipe it for the week before or after), then one column per person for the day picked, each
 * with how many of their visits are done. A visit card shows the job, the client, the time and the address; done ones
 * are grayed with a check. Tap a card to open its visit page, like Jobber's phone app (owner, 2026-10-30: the sheet "opens
 * weird"). Visits with nobody on them get their own column. View
 * options hides people and weekends, and is kept on this phone. Crew keep their column on days with no work, so the board
 * shows who is free (owner, 2026-10-30). A new week's day numbers slide in from the side they come from, like the old
 * Steward's (02-render.js calSlideOn, owner: "I didnt like how it just teleported"); the letters and the board stay put
 * (owner, 2026-10-30: "only the numbers swipe"). One or two people fill the width; three or more scroll sideways.
 */
import { addDays, dayVisits, mondayOf, type VisitCard } from "@steward/shared";
import { Link, useNavigate } from "@tanstack/react-router";
import { useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type TouchEvent } from "react";
import { useMySchedule, useVisits, type VisitsQuery } from "../api/hooks";
import { crewWords, useSession } from "../app/session";
import { useWordColor } from "../components/wordColor";
import { dayDate, monthName, plural, timeRange, todayIn } from "../components/format";
import { Icon } from "../components/Icon";
import { Avatar, LoadError, Skeleton } from "../components/parts";

const LETTERS = ["M", "T", "W", "T", "F", "S", "S"];
const NOBODY = "none";

interface Opts {
  weekends: boolean;
  hide: string[];
  showOff: boolean;
}
const KEY = "steward.schedule.phone";
function readOpts(): Opts {
  try {
    const o = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Partial<Opts>;
    return { weekends: o.weekends ?? true, hide: Array.isArray(o.hide) ? o.hide : [], showOff: Boolean(o.showOff) };
  } catch {
    return { weekends: true, hide: [], showOff: false };
  }
}

const kindOf = (v: VisitCard) => (v.commercial ? (v.oneTime ? "com" : "rcom") : v.oneTime ? "one" : "rec");

/** "October", or "January 2027" outside this year. */
function monthWord(d: string, today: string): string {
  const m = monthName(d);
  return d.slice(0, 4) === today.slice(0, 4) ? m.replace(/ \d{4}$/, "") : m;
}

/**
 * onRoute (routes on) opens the picked day's Route view: stops in driving order with the map (RouteDay.tsx). mine: a crew
 * member's own board (owner, Oct 31: "it should look like mine just certain things aren't visible for them"): only their
 * visits, their one column, no people options, and a card opens their visit page. With routes on they get the Route
 * button too, to their own stops (CrewRoute.tsx, owner Oct 31).
 */
export function SchedulePhone({ onRoute, mine = false }: { onRoute?: (day: string) => void; mine?: boolean } = {}) {
  const { company, me } = useSession();
  const words = crewWords(me.locale);
  const today = todayIn(company.timezone);
  const [sel, setSel] = useState(today);
  const [monthAt, setMonthAt] = useState<string | null>(null);
  const [optsOpen, setOptsOpen] = useState(false);
  const [opts, setOptsState] = useState(readOpts);
  const navigate = useNavigate();
  const touch = useRef<{ x: number; y: number } | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const setOpts = (o: Opts) => {
    setOptsState(o);
    try {
      localStorage.setItem(KEY, JSON.stringify(o));
    } catch {
      // Kept until the page closes.
    }
  };

  const monday = mondayOf(sel);
  const query: VisitsQuery = { from: monday, to: addDays(monday, 6) };
  const office = useVisits(company.id, query, !mine);
  const own = useMySchedule(company.id, query, mine);
  const visits = mine ? own : office;
  // The weeks either side load ahead, so a swipe lands on its visits instead of last week's.
  const next = { from: addDays(monday, 7), to: addDays(monday, 13) };
  const last = { from: addDays(monday, -7), to: addDays(monday, -1) };
  useVisits(company.id, next, !mine);
  useVisits(company.id, last, !mine);
  useMySchedule(company.id, next, mine);
  useMySchedule(company.id, last, mine);
  useWeekSlide(root, monday);
  const list = visits.data?.visits ?? [];
  const day = dayVisits(list, sel, opts.showOff);
  const week = Array.from({ length: 7 }, (_, i) => addDays(monday, i)).filter((d, i) => opts.weekends || i < 5 || d === sel);
  const all = visits.data?.people ?? [];
  const busy = busyPeople(all, list);
  const people = busy.filter((p) => !opts.hide.includes(p.id));
  const hidden = busy.length - people.length;

  const swipe = {
    onTouchStart: (e: TouchEvent) => {
      const t = e.touches[0];
      touch.current = t ? { x: t.clientX, y: t.clientY } : null;
    },
    onTouchEnd: (e: TouchEvent) => {
      const s = touch.current;
      const t = e.changedTouches[0];
      touch.current = null;
      if (!s || !t) return;
      const dx = t.clientX - s.x;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(t.clientY - s.y)) setSel(addDays(sel, dx < 0 ? 7 : -7));
    },
  };

  return (
    <div className="psched" ref={root}>
      <div className="psched-head">
        <button
          type="button"
          className="psched-month"
          aria-expanded={monthAt !== null}
          onClick={() => setMonthAt(monthAt === null ? `${sel.slice(0, 8)}01` : null)}
        >
          <span>{monthWord(sel, today)}</span>
          <Icon name={monthAt === null ? "chevronDown" : "chevronUp"} size={20} />
        </button>
        <div className="psched-tools">
          <button type="button" className="psched-today" onClick={() => setSel(today)}>
            {mine ? words.today : "Today"}
          </button>
          {onRoute ? (
            <button
              type="button"
              className="psched-icon"
              aria-label={mine && me.locale === "es" ? "Ruta" : "Route"}
              onClick={() => onRoute(sel)}
            >
              <Icon name="route" size={22} />
            </button>
          ) : null}
          <button
            type="button"
            className="psched-icon"
            aria-label="View options"
            aria-expanded={optsOpen}
            onClick={() => setOptsOpen(!optsOpen)}
          >
            <Icon name="sliders" size={22} />
          </button>
        </div>
      </div>
      {optsOpen ? (
        <div className="psched-opts">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <b>View options</b>
            <button type="button" className="link" onClick={() => setOptsOpen(false)}>
              Done
            </button>
          </div>
          <label className="row">
            <input type="checkbox" checked={opts.weekends} onChange={(e) => setOpts({ ...opts, weekends: e.target.checked })} />
            Show weekends
          </label>
          <label className="row">
            <input type="checkbox" checked={opts.showOff} onChange={(e) => setOpts({ ...opts, showOff: e.target.checked })} />
            Show skipped and canceled
          </label>
          {all.length && !mine ? (
            <>
              <b className="small">People</b>
              {all.map((p) => (
                <label key={p.id} className="row">
                  <input
                    type="checkbox"
                    checked={!opts.hide.includes(p.id)}
                    onChange={(e) =>
                      setOpts({
                        ...opts,
                        hide: e.target.checked ? opts.hide.filter((x) => x !== p.id) : [...opts.hide, p.id],
                      })
                    }
                  />
                  {p.name}
                </label>
              ))}
            </>
          ) : null}
          <span className="small muted">Kept on this phone.</span>
          {mine ? null : <Link to="/schedule/tools">Routes and day tools</Link>}
        </div>
      ) : null}
      {monthAt !== null ? (
        <MonthPick
          at={monthAt}
          sel={sel}
          today={today}
          onStep={(n) => setMonthAt(shiftMonth(monthAt, n))}
          onPick={(d) => {
            setSel(d);
            setMonthAt(null);
          }}
        />
      ) : (
        <div className="psched-strip" {...swipe} role="group" aria-label="Days of the week">
          {week.map((d) => (
            <button
              key={d}
              type="button"
              className={`psched-day${d === sel ? " sel" : ""}${d === today ? " today" : ""}`}
              aria-pressed={d === sel}
              aria-label={dayDate(d)}
              onClick={() => setSel(d)}
            >
              <span>{LETTERS[(new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7]}</span>
              <b className="num">{Number(d.slice(8))}</b>
            </button>
          ))}
        </div>
      )}
      {visits.error ? (
        <LoadError error={visits.error} retry={() => void visits.refetch()} />
      ) : !visits.data ? (
        <Skeleton rows={4} />
      ) : (
        <DayBoard
          day={day}
          people={people}
          today={today}
          refreshing={visits.isPlaceholderData}
          onPick={(v) =>
            void (mine
              ? navigate({ to: "/my-day/$ref", params: { ref: v.ref } })
              : navigate({ to: "/visits/$ref", params: { ref: v.ref } }))
          }
        />
      )}
      {hidden > 0 ? (
        <p className="small muted psched-note">
          {hidden === 1 ? "1 person is" : `${String(hidden)} people are`} hidden under View options.
        </p>
      ) : null}
    </div>
  );
}

/** Slides the week's day numbers in from the right for a later week, from the left for an earlier one, as one row. */
function useWeekSlide(root: { current: HTMLElement | null }, monday: string) {
  const was = useRef(monday);
  useLayoutEffect(() => {
    const from = was.current;
    was.current = monday;
    if (from === monday || !root.current?.animate) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const strip = root.current.querySelector(".psched-strip");
    if (!strip) return;
    const x = `${String(monday > from ? strip.clientWidth : -strip.clientWidth)}px`;
    for (const el of strip.querySelectorAll(".psched-day b")) {
      el.animate(
        [
          { transform: `translateX(${x})`, opacity: 0.4 },
          { transform: "none", opacity: 1 },
        ],
        {
          duration: 380,
          easing: "cubic-bezier(.25,.75,.3,1)",
        },
      );
    }
  }, [root, monday]);
}

/**
 * Crew always, so a free cleaner still shows (owner, 2026-10-30: "see availability on the go"), plus office staff with
 * work this week. Everyone when that leaves nobody.
 */
export function busyPeople<P extends { id: string; role?: string | undefined }>(all: P[], week: VisitCard[]): P[] {
  const busy = all.filter((p) => p.role === "crew" || week.some((v) => v.crew.some((c) => c.id === p.id)));
  return busy.length ? busy : all;
}

/**
 * One day with a column per person, each with how many of their visits are done, and a column for visits with nobody on
 * them. The phone Schedule and the List view on bigger screens both use it. card adds to each card (drag, right-click).
 */
export function DayBoard({
  day,
  people,
  today,
  refreshing,
  onPick,
  card: extra,
}: {
  day: VisitCard[];
  people: { id: string; name: string; color?: string | null; photo?: string | null }[];
  today: string;
  refreshing?: boolean;
  onPick: (v: VisitCard, at: DOMRect) => void;
  card?: (v: VisitCard) => ButtonHTMLAttributes<HTMLButtonElement>;
}) {
  const wordColor = useWordColor();
  const nobody = day.filter((v) => !v.crew.length);
  const cols = [
    ...(nobody.length || !people.length ? [{ id: NOBODY, name: people.length ? "No crew yet" : "Visits", here: nobody }] : []),
    ...people.map((p) => ({ ...p, here: day.filter((v) => v.crew.some((c) => c.id === p.id)) })),
  ];
  if (!people.length && cols[0]) cols[0].here = day;

  const card = (v: VisitCard) => {
    const done = v.status === "done";
    const off = v.status === "skipped" || v.status === "cancelled";
    const late = v.date < today && v.status === "scheduled";
    return (
      <button
        type="button"
        key={v.ref}
        style={wordColor(v)}
        className={`pcard kind-${kindOf(v)}${done ? " done" : ""}${off ? " off" : ""}${late ? " late" : ""}`}
        onClick={(e) => onPick(v, e.currentTarget.getBoundingClientRect())}
        {...extra?.(v)}
      >
        <b>{v.jobName}</b>
        {done ? <Icon name="check" size={16} label="Done" /> : null}
        <span className="pcard-client">{v.clientName}</span>
        <span className="num pcard-time">
          {v.startTime ? timeRange(v.startTime, v.endTime).replace(" to ", " - ") : "Any time"}
        </span>
        {late ? <span className="pcard-late">Not marked done</span> : null}
        {v.unconfirmed && !late ? <span className="pcard-late">Not confirmed</span> : null}
        {v.address ? <span className="pcard-addr">{v.address}</span> : null}
      </button>
    );
  };

  return (
    <div
      className={`psched-board${cols.length === 1 ? " solo" : cols.length === 2 ? " pair" : ""}${refreshing ? " refreshing" : ""}`}
    >
      {cols.map((c) => {
        const done = c.here.filter((v) => v.status === "done").length;
        return (
          <section key={c.id} className="psched-col" aria-label={`${c.name}, ${plural(c.here.length, "visit")}`}>
            <header>
              {c.id === NOBODY ? null : <Avatar name={c.name} person={c} />}
              <b>{c.name}</b>
              <span className={`psched-count num${c.here.length && done === c.here.length ? " all" : ""}`}>
                {c.here.length ? `${String(done)}/${String(c.here.length)}` : "0"}
              </span>
            </header>
            <div className="psched-cards">
              {c.here.length ? (
                c.here.map(card)
              ) : (
                <div className="psched-none">
                  <Icon name="schedule" size={32} />
                  <b>Nothing booked</b>
                </div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

/** The 1st of the month n months from a month's 1st. */
function shiftMonth(first: string, n: number): string {
  const total = Number(first.slice(0, 4)) * 12 + Number(first.slice(5, 7)) - 1 + n;
  return `${String(Math.floor(total / 12))}-${String((total % 12) + 1).padStart(2, "0")}-01`;
}

/** The month under the month button: Monday first, the days of the months either side shaded, today underlined. */
function MonthPick({
  at,
  sel,
  today,
  onStep,
  onPick,
}: {
  at: string;
  sel: string;
  today: string;
  onStep: (n: 1 | -1) => void;
  onPick: (d: string) => void;
}) {
  const start = mondayOf(at);
  const end = addDays(shiftMonth(at, 1), -1);
  const days: string[] = [];
  for (let d = start; d <= end || days.length % 7; d = addDays(d, 1)) days.push(d);
  return (
    <div className="psched-mcal">
      <div className="psched-mtop">
        <b>{monthName(at)}</b>
        <span>
          <button type="button" className="psched-icon" aria-label="Month before" onClick={() => onStep(-1)}>
            <Icon name="chevronLeft" />
          </button>
          <button type="button" className="psched-icon" aria-label="Next month" onClick={() => onStep(1)}>
            <Icon name="chevronRight" />
          </button>
        </span>
      </div>
      <div className="psched-mgrid">
        {LETTERS.map((l, i) => (
          <span key={i} className="psched-mdow" aria-hidden>
            {l}
          </span>
        ))}
        {days.map((d) => (
          <button
            key={d}
            type="button"
            className={`psched-md num${d.slice(0, 7) !== at.slice(0, 7) ? " out" : ""}${d === today ? " today" : ""}${d === sel ? " sel" : ""}`}
            aria-pressed={d === sel}
            aria-label={dayDate(d)}
            onClick={() => onPick(d)}
          >
            {Number(d.slice(8))}
          </button>
        ))}
      </div>
    </div>
  );
}
